import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { CitationList } from "@/components/common";
import { AlertTriangle, ChevronDown, ChevronRight, UserRound } from "lucide-react";

/**
 * Workstream C (2026-09) — ONE renderer for a Consultant Coaching document,
 * shared by Consultant Performance (Leadership) and My Coaching (employee).
 *
 * Round 12, Workstream G redesign — confirmed production UAT finding: the
 * most actionable observations (clear overdue follow-ups, missing notes,
 * undone follow-up tasks) were genuinely present but buried beneath scores,
 * caveats and lengthy evidence. The view now always leads with a short,
 * always-visible, product-aware 5-part executive summary
 * (`doc.executive_summary_sections`, backend-composed — see
 * consultant_coaching._compose_executive_summary), and everything else
 * (customer-level evidence, cross-customer patterns, the structured action
 * plan, supporting evidence) moves into a single collapsed "Full detail"
 * section beneath it:
 *   0. Executive summary — What went well / What needs improvement / What
 *      to do this week / Manager attention (leadership only, only when
 *      genuinely required) / Confidence and data limitations.
 *   1..6 (collapsed by default) — the pre-existing detailed sections,
 *      unchanged: did well, requiring attention, cross-customer patterns,
 *      seven-day action plan, supporting evidence.
 * A document generated before this redesign (schema_version < 5) has no
 * NATIVE `executive_summary_sections` cached at generation time — but
 * Round 13, item 5 fixed the resulting production gap (a released
 * document that still showed "Limited evidence-based fallback"/"Earlier
 * coaching format"/long defensive text with no five-part summary): every
 * backend read path now derives the same five-part shape ON READ from
 * that document's own already-persisted fields (see consultant_coaching.
 * attach_legacy_executive_summary/_compose_legacy_executive_summary) and
 * attaches it under the SAME `executive_summary_sections` key, marked
 * `legacy_derived: true` so this renderer can show it's a compatibility
 * view rather than the model's own native output — see the small badge
 * in ExecutiveSummarySections below. `hasNewSummary` (and therefore the
 * single-paragraph fallback further down) is now reached only for a
 * document with NO coaching content at all to derive from (e.g. one
 * already scrubbed as `legacy_corrupted`).
 * Evidence is always shown as the record's human-readable label linking to
 * the actual record (Customer 360) — never an internal evidence id in text.
 * Recommended wording is always labelled as something to TRY, never as a
 * quote from a real conversation.
 */
const TECHNIQUE_LABEL = {
  discovery: "Discovery", objection_handling: "Objection handling", commitment: "Commitment",
  follow_up: "Follow-up", documentation: "Documentation",
};
const GENERIC_UNASSESSED = "Not enough evidence was available to assess this automatically.";
const CONFIDENCE_TONE = {
  low: "bg-slate-500/15 text-slate-600 dark:text-slate-400",
  medium: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  high: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
};

const fmtDay = (iso) => (iso ? new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric",
}).format(new Date(iso)) : "—");

// period_end is exclusive — the last included day is one instant earlier.
export const coachingPeriodLabel = (doc) => {
  if (!doc?.period_start || !doc?.period_end) return null;
  const endInclusive = new Date(new Date(doc.period_end).getTime() - 1).toISOString();
  return `${fmtDay(doc.period_start)} – ${fmtDay(endInclusive)} (IST)`;
};

// One citation per distinct record label: a finding never lists the same
// customer/activity twice, and the count never inflates.
const uniqueCitations = (ids, byEvidenceId) => {
  const seen = new Set();
  const out = [];
  for (const id of ids || []) {
    const c = byEvidenceId?.[id];
    if (!c || seen.has(c.text)) continue;
    seen.add(c.text);
    out.push(c);
  }
  return out;
};

const EvidenceLinks = ({ ids, byEvidenceId }) => {
  const citations = uniqueCitations(ids, byEvidenceId);
  if (citations.length === 0) return null;
  return (
    <div className="pt-0.5">
      <CitationList citations={citations} testId="finding-evidence-links" />
    </div>
  );
};

const groupByCustomer = (items) => {
  const groups = new Map();
  for (const it of items || []) {
    const key = it.customer_label || "Customer not resolved";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(it);
  }
  return Array.from(groups.entries());
};

const SectionTitle = ({ n, children }) => (
  <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-1.5">
    <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-muted text-[9px] text-foreground">{n}</span>
    {children}
  </p>
);

const Label = ({ children }) => <span className="text-muted-foreground">{children} </span>;

const ShowAllToggle = ({ total, shown, expanded, onToggle, testId }) => (total > shown ? (
  <button type="button" onClick={onToggle} data-testid={testId}
    className="text-[11px] text-muted-foreground hover:text-foreground underline">
    {expanded ? "Show fewer" : `Show all ${total}`}
  </button>
) : null);

const CustomerGroups = ({ items, limit, testId, tone, renderItem, toggleTestId }) => {
  const [expanded, setExpanded] = useState(false);
  const groups = groupByCustomer(items);
  const shown = expanded ? groups : groups.slice(0, limit);
  return (
    <div className="space-y-2" data-testid={testId}>
      {shown.map(([customer, its]) => (
        <div key={customer} className={`rounded-md border-l-2 ${tone} bg-muted/30 px-2.5 py-2 space-y-1.5`}
          data-testid="coaching-customer-card">
          <p className="text-xs font-medium flex items-center gap-1 break-words">
            <UserRound className="h-3 w-3 shrink-0 text-muted-foreground" />{customer}
          </p>
          {its.map((it, i) => <div key={i} className="space-y-0.5">{renderItem(it)}</div>)}
        </div>
      ))}
      <ShowAllToggle total={groups.length} shown={limit} expanded={expanded}
        onToggle={() => setExpanded(!expanded)} testId={toggleTestId} />
    </div>
  );
};

const SuggestedWording = ({ text }) => (text ? (
  <div className="rounded border border-dashed px-2 py-1" data-testid="suggested-wording">
    <p className="text-[10px] text-muted-foreground">Suggested wording to try (a recommendation — not from a real conversation)</p>
    <p className="italic break-words">“{text}”</p>
  </div>
) : null);

const SupportingEvidence = ({ doc, byEvidenceId }) => {
  const [expanded, setExpanded] = useState(false);
  const entries = useMemo(() => {
    const c = doc.coaching || {};
    const refs = new Map(); // evidence id -> set of section names
    const note = (ids, section) => (ids || []).forEach((id) => {
      if (!refs.has(id)) refs.set(id, new Set());
      refs.get(id).add(section);
    });
    (c.doing_well || []).forEach((it) => note(it.evidence_ids, "Did well"));
    (c.customer_opportunities || []).forEach((it) => note(it.evidence_ids, "Needs attention"));
    (c.improve || []).forEach((it) => note(it.evidence_ids, "Patterns"));
    (c.seven_day_action_plan || []).forEach((it) => note(it.evidence_ids, "Action plan"));
    // Deduplicate by record label: one canonical entry per record, listing
    // every section that referenced it.
    const byText = new Map();
    for (const [id, citation] of Object.entries(byEvidenceId || {})) {
      if (!byText.has(citation.text)) byText.set(citation.text, { citation, sections: new Set() });
      (refs.get(id) || []).forEach((s) => byText.get(citation.text).sections.add(s));
    }
    return Array.from(byText.values());
  }, [doc, byEvidenceId]);
  if (entries.length === 0) return null;
  const customers = new Set(entries.map((e) => (e.citation.text || "").split(" · ")[0]));
  return (
    <div data-testid="coaching-supporting-evidence">
      <button type="button" onClick={() => setExpanded(!expanded)} data-testid="all-evidence-toggle"
        className="flex items-center gap-1 text-left text-[11px] uppercase tracking-wide text-muted-foreground hover:text-foreground">
        <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-muted text-[9px] text-foreground">6</span>
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Supporting evidence ({entries.length} record{entries.length === 1 ? "" : "s"}, {customers.size} customer{customers.size === 1 ? "" : "s"})
      </button>
      {expanded && (
        <ul className="mt-1.5 space-y-1 pl-5" data-testid="all-evidence-grouped">
          {entries.map((e) => (
            <li key={e.citation.text} className="flex flex-wrap items-center gap-1.5">
              <CitationList citations={[e.citation]} testId="coaching-citations" />
              {Array.from(e.sections).map((s) => (
                <Badge key={s} variant="outline" className="text-[9px] font-normal">{s}</Badge>
              ))}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Round 12, Workstream G — the redesigned top-of-document executive
// summary. Every part is already-governed content the backend recombined
// deterministically (consultant_coaching._compose_executive_summary) —
// this component only lays it out, never re-derives or filters content.
const ExecutiveSummaryBullets = ({ items, testId, tone }) => (
  <ul className={`space-y-1 list-disc pl-4 ${tone || ""}`} data-testid={testId}>
    {(items || []).map((text, i) => <li key={i} className="break-words">{text}</li>)}
  </ul>
);

const ExecutiveSummarySections = ({ sections, showManagementNotes, productNames }) => (
  <section className="space-y-2.5" data-testid="coaching-executive-summary">
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <SectionTitle n={1}>Executive summary</SectionTitle>
      <div className="flex items-center gap-1.5 flex-wrap">
        {sections.legacy_derived && (
          <Badge variant="outline" className="text-[10px]" data-testid="coaching-summary-legacy-derived">
            Compatibility view — derived from the original document
          </Badge>
        )}
        {productNames?.length > 0 && (
          <span className="text-[10px] text-muted-foreground" data-testid="coaching-summary-products">
            {productNames.join(" · ")}
          </span>
        )}
      </div>
    </div>
    <div>
      <p className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400">What went well</p>
      <ExecutiveSummaryBullets items={sections.what_went_well} testId="summary-what-went-well" />
    </div>
    <div>
      <p className="text-[11px] font-medium text-amber-700 dark:text-amber-400">What needs improvement</p>
      <ExecutiveSummaryBullets items={sections.what_needs_improvement} testId="summary-what-needs-improvement" />
    </div>
    <div>
      <p className="text-[11px] font-medium">What to do this week</p>
      <ExecutiveSummaryBullets items={sections.what_to_do_this_week} testId="summary-what-to-do-this-week" />
    </div>
    {showManagementNotes && sections.manager_attention?.length > 0 && (
      <div>
        <div className="text-[11px] font-medium flex items-center gap-1.5 flex-wrap">
          Manager attention <Badge variant="outline" className="text-[9px]">never shown to the consultant</Badge>
        </div>
        <ExecutiveSummaryBullets items={sections.manager_attention} testId="summary-manager-attention" />
      </div>
    )}
    {sections.confidence_and_limitations && (
      <p className="text-muted-foreground break-words" data-testid="summary-confidence-and-limitations">
        {sections.confidence_and_limitations}
      </p>
    )}
  </section>
);

export const CoachingDocumentView = ({ doc, byEvidenceId, showManagementNotes = false }) => {
  const c = doc.coaching;
  const [showTechniqueNotes, setShowTechniqueNotes] = useState(false);
  const qctx = doc.quality_context;
  const commercial = doc.commercial_context;
  const sections = doc.executive_summary_sections;
  const hasNewSummary = !!sections;
  const summary = c.executive_summary
    || [c.overall_read?.strengths_summary, c.overall_read?.biggest_opportunity].filter(Boolean).join(" ");
  const actionPlan = [...(c.seven_day_action_plan || [])].sort((a, b) => (a.due_within_days || 7) - (b.due_within_days || 7));
  const techniqueNotes = [
    ["Discovery", c.discovery?.question_quality], ["Motivation", c.discovery?.motivation_discovery],
    ["Decision-maker", c.discovery?.decision_maker_discovery],
    ["Commitment", c.commitment_and_closing?.commitment_seeking],
    ["Follow-up timing", c.followup_coaching?.timing],
    ["Objections", (c.objection_handling?.missed_opportunities || [])[0]],
  ].filter(([, v]) => v && v !== GENERIC_UNASSESSED);
  const period = coachingPeriodLabel(doc);
  // Detail (sections 2-6) defaults collapsed once the new top summary
  // already carries the primary content — a legacy document (no new
  // summary to lead with) shows its detail expanded by default instead.
  const [detailExpanded, setDetailExpanded] = useState(!hasNewSummary);

  return (
    <div className="space-y-3 text-xs" data-testid={`coaching-doc-${doc.coaching_id}`}>
      <div className="flex items-center gap-1.5 flex-wrap">
        {doc.confidence && <Badge className={CONFIDENCE_TONE[doc.confidence] || ""}>{doc.confidence} confidence</Badge>}
        {qctx?.provisional && (
          <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-700 dark:text-amber-400"
            data-testid="coaching-provisional-badge">Provisional evidence</Badge>
        )}
        {doc.generation_mode === "deterministic_safe_fallback" && (
          <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-700 dark:text-amber-400"
            data-testid="coaching-fallback-label">Limited evidence-based fallback</Badge>
        )}
        {(doc.schema_version ?? 1) < 4 && <Badge variant="outline" className="text-[10px]">Earlier coaching format</Badge>}
        {period && <span className="text-[10px] text-muted-foreground" data-testid="coaching-period">{period}</span>}
      </div>

      {/* 0 — Redesigned executive summary (Round 12, Workstream G) */}
      {hasNewSummary ? (
        <ExecutiveSummarySections sections={sections} showManagementNotes={showManagementNotes}
          productNames={sections.product_names} />
      ) : (
        // Legacy fallback (schema_version < 5 — no executive_summary_sections
        // was ever computed for this document): the original single-
        // paragraph summary, unchanged.
        <section className="space-y-1.5" data-testid="coaching-executive-summary">
          <SectionTitle n={1}>Executive summary</SectionTitle>
          <p className="text-sm leading-relaxed break-words">{summary}</p>
        </section>
      )}
      {qctx?.evidence_warning && (
        <p className="flex gap-1.5 rounded border border-amber-500/40 bg-amber-500/5 px-2 py-1.5 text-amber-800 dark:text-amber-300"
          data-testid="coaching-evidence-warning">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span className="break-words">{qctx.evidence_warning}</span>
        </p>
      )}
      {doc.generation_mode === "deterministic_safe_fallback" && !hasNewSummary && (
        <p className="text-amber-700 dark:text-amber-400" data-testid="coaching-fallback-warning">
          Detailed AI analysis could not be completed for this period — this is a limited,
          metrics-only summary, not a full AI coaching analysis.
        </p>
      )}
      {commercial && (
        <p className="text-muted-foreground" data-testid="coaching-commercial-line">
          Commercial conversions credited this period: <span className="font-medium text-foreground">{commercial.conversion_count}</span> — {commercial.note}
        </p>
      )}

      {/* Sections 2-6: full detail, collapsed by default once the new
          summary above already leads with the primary content. */}
      <button type="button" onClick={() => setDetailExpanded(!detailExpanded)}
        className="flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground hover:text-foreground"
        data-testid="coaching-detail-toggle">
        {detailExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        Full detail — customer evidence, patterns, scoring and action plan
      </button>
      {detailExpanded && (
      <div className="space-y-3" data-testid="coaching-detail-body">
      {/* 2 — Did well */}
      {c.doing_well?.length > 0 && (
        <section className="space-y-1.5">
          <SectionTitle n={2}>Customers where {showManagementNotes ? "they" : "you"} did well</SectionTitle>
          <CustomerGroups items={c.doing_well} limit={3} testId="coaching-doing-well"
            tone="border-emerald-500/50" toggleTestId="doing-well-show-all-toggle"
            renderItem={(it) => (<>
              <p className="break-words"><Label>What:</Label>{it.behavior}</p>
              {it.evidence && <p className="break-words"><Label>Why it helped:</Label>{it.evidence}</p>}
              {it.repeat_this && <p className="break-words"><Label>Repeat it:</Label>{it.repeat_this}</p>}
              <EvidenceLinks ids={it.evidence_ids} byEvidenceId={byEvidenceId} />
            </>)} />
        </section>
      )}

      {/* 3 — Requiring attention */}
      {c.customer_opportunities?.length > 0 && (
        <section className="space-y-1.5">
          <SectionTitle n={3}>Customers requiring attention</SectionTitle>
          <CustomerGroups items={c.customer_opportunities} limit={5} testId="coaching-customer-opportunities"
            tone="border-amber-500/50" toggleTestId="attention-show-all-toggle"
            renderItem={(it) => (<>
              {it.sales_technique && (
                <Badge variant="outline" className="text-[9px]" data-testid="coaching-technique">
                  {TECHNIQUE_LABEL[it.sales_technique] || it.sales_technique}
                </Badge>
              )}
              {it.current_situation && <p className="text-muted-foreground break-words">{it.current_situation}</p>}
              <p className="break-words"><Label>Missing / weak:</Label>{it.could_improve || it.concern_or_commitment}</p>
              {it.why_it_matters && <p className="break-words"><Label>Why it matters:</Label>{it.why_it_matters}</p>}
              <p className="break-words"><Label>Next action:</Label>{it.recommended_next_action}</p>
              {it.done_well_if_any && <p className="text-muted-foreground break-words"><Label>Already done well:</Label>{it.done_well_if_any}</p>}
              <SuggestedWording text={it.suggested_conversation} />
              <EvidenceLinks ids={it.evidence_ids} byEvidenceId={byEvidenceId} />
            </>)} />
        </section>
      )}

      {/* 4 — Cross-customer patterns */}
      {(c.improve?.length > 0 || techniqueNotes.length > 0) && (
        <section className="space-y-1.5" data-testid="coaching-cross-customer-patterns">
          <SectionTitle n={4}>Cross-customer patterns</SectionTitle>
          <ul className="space-y-2">
            {(c.improve || []).map((it, i) => (
              <li key={i} className="space-y-0.5" data-testid="coaching-pattern">
                <p className="font-medium break-words">{it.behavior_observed}</p>
                {it.customer_labels?.length > 0 && (
                  <p className="text-muted-foreground break-words" data-testid="coaching-pattern-customers">
                    Seen for: {it.customer_labels.join("; ")}
                  </p>
                )}
                {it.why_it_matters && <p className="break-words"><Label>Why it matters:</Label>{it.why_it_matters}</p>}
                {it.what_to_do_differently && <p className="break-words"><Label>Try instead:</Label>{it.what_to_do_differently}</p>}
                <SuggestedWording text={it.example_wording} />
              </li>
            ))}
          </ul>
          {techniqueNotes.length > 0 && (
            <div>
              <button type="button" onClick={() => setShowTechniqueNotes(!showTechniqueNotes)}
                className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1"
                data-testid="coaching-technique-notes-toggle">
                {showTechniqueNotes ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                Technique notes ({techniqueNotes.length})
              </button>
              {showTechniqueNotes && (
                <ul className="mt-1 space-y-0.5 pl-4">
                  {techniqueNotes.map(([k, v]) => <li key={k} className="break-words"><Label>{k}:</Label>{v}</li>)}
                </ul>
              )}
            </div>
          )}
        </section>
      )}

      {/* 5 — Seven-day action plan */}
      {(actionPlan.length > 0 || c.practice_recommendations?.length > 0) && (
        <section className="space-y-1.5" data-testid="coaching-action-plan">
          <SectionTitle n={5}>Seven-day action plan</SectionTitle>
          {actionPlan.length > 0 && (
            <ol className="space-y-1.5">
              {actionPlan.map((a, i) => (
                <li key={i} className="flex gap-2" data-testid="coaching-action-item">
                  <Badge variant="outline" className="text-[9px] h-fit shrink-0">By day {a.due_within_days || 7}</Badge>
                  <div className="space-y-0.5 min-w-0">
                    <p className="break-words">{a.action}{a.target_count ? <span className="text-muted-foreground"> (target: {a.target_count})</span> : null}</p>
                    <p className="text-muted-foreground break-words">Done when: {a.success_measure}</p>
                    <EvidenceLinks ids={a.evidence_ids} byEvidenceId={byEvidenceId} />
                  </div>
                </li>
              ))}
            </ol>
          )}
          {c.practice_recommendations?.length > 0 && (
            <div className="space-y-1" data-testid="coaching-actionable-ai-coaching">
              <p className="text-[10px] text-muted-foreground">Techniques to practise</p>
              <ul className="space-y-1">
                {c.practice_recommendations.map((p, i) => (
                  <li key={i} className="space-y-0.5">
                    <div className="break-words">
                      {p.technique && <Badge variant="outline" className="text-[9px] mr-1">{TECHNIQUE_LABEL[p.technique] || p.technique}</Badge>}
                      {p.skill}
                    </div>
                    <SuggestedWording text={p.example_phrasing} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* 6 — Supporting evidence (collapsed, deduplicated) */}
      <SupportingEvidence doc={doc} byEvidenceId={byEvidenceId} />

      {/* Legacy documents (no executive_summary_sections) had no "Manager
          attention" part in the summary above — keep the original
          standalone block for them only, so nothing is silently dropped. */}
      {!hasNewSummary && showManagementNotes && c.management_notes?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1 flex-wrap">
            Manager-only notes <Badge variant="outline" className="text-[9px]">never shown to the consultant</Badge>
          </p>
          <ul className="list-disc pl-4 space-y-0.5">
            {c.management_notes.map((n, i) => <li key={i} className="break-words">{n.observation}</li>)}
          </ul>
        </div>
      )}
      </div>
      )}
    </div>
  );
};
